import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Divider,
  Paper,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  FactCheck as CountIcon,
  Done as CompleteIcon,
  Warning as DiscrepancyIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  CalendarToday as CalendarIcon,
  Warehouse as WarehouseIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useBrand } from '../../context/BrandContext';
import { useInventoryBrand } from '../../hooks/useInventoryBrand';
import { excludeCompanyLevel } from '../../utils/brandFilters';
import {
  getWarehouses,
  getCalculatedStockLevels,
  getSkuBatches,
  formatNumber,
  formatDate,
  formatCurrency
} from '../../services/inventoryService';
import api from '../../config/api';

const API_BASE = process.env.REACT_APP_API_URL || '';

function PhysicalCounts({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBrand, brands, selectBrand } = useInventoryBrand();
  const selectedBrandId = selectedBrand?.id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [warning, setWarning] = useState(null);

  // Step tracking - read from URL or default to 1
  const [currentStep, setCurrentStep] = useState(() => {
    const stepParam = searchParams.get('step');
    return stepParam ? parseInt(stepParam, 10) : 1;
  });

  // Setup form - read from URL params
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(() => searchParams.get('warehouse') || '');
  const [countDate, setCountDate] = useState(() => searchParams.get('date') || new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  // Update URL when parameters change
  const updateUrlParams = useCallback((params) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Inventory data
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  // Signature of the brand|warehouse|date combo we've already auto-loaded for.
  // Guards the URL-driven auto-load effect from re-firing when a warehouse
  // genuinely has 0 calculated items (length stays 0, so the old
  // `inventoryItems.length === 0` guard looped and kept re-forcing step 2,
  // which made the Cancel button appear dead). Reset on cancel / param change.
  const autoLoadSignatureRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSku, setExpandedSku] = useState(null);
  const [skuBatches, setSkuBatches] = useState({});
  // sku -> message when that date's stock could not be read (server busy, DT-892): shown with a retry, not as "no batches"
  const [skuBatchErrors, setSkuBatchErrors] = useState({});
  const [skuBatchLoading, setSkuBatchLoading] = useState({});
  // Bumped whenever a reload clears the batch cache (DT-914). A batch read that started before the bump belongs to the
  // previous count (date / warehouse / brand) and is dropped when it answers.
  const batchGenRef = useRef(0);

  // Count data - stores user's counted values
  const [countedData, setCountedData] = useState({}); // { sku: { total: number, batches: [{ batch_number, expiry_date, quantity }] } }

  // System batch edits - stores user's edits to system batch quantities
  // { sku: { batchIndex: { editedQty: number, varianceRemark: string } } }
  const [systemBatchEdits, setSystemBatchEdits] = useState({});
  const [editingSystemBatch, setEditingSystemBatch] = useState(null); // { sku, batchIndex }

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Dialogs
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [addBatchDialogOpen, setAddBatchDialogOpen] = useState(false);
  const [currentSkuForBatch, setCurrentSkuForBatch] = useState(null);
  const [newBatch, setNewBatch] = useState({ batch_number: '', expiry_date: '', quantity: '' });

  // Past counts
  const [pastCounts, setPastCounts] = useState([]);
  const [showPastCounts, setShowPastCounts] = useState(false);

  // Track if we've initialized from URL params
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);

  // Restore brand from URL on initial load
  useEffect(() => {
    const brandIdParam = searchParams.get('brand_id');
    if (brandIdParam && brands.length > 0 && !initializedFromUrl) {
      const brandId = parseInt(brandIdParam, 10);
      const brand = brands.find(b => b.id === brandId);
      if (brand && (!selectedBrand || selectedBrand.id !== brandId)) {
        selectBrand(brand);
      }
      setInitializedFromUrl(true);
    }
  }, [brands, searchParams, selectBrand, selectedBrand, initializedFromUrl]);

  useEffect(() => {
    loadWarehouses();
    loadPastCounts();
  }, [selectedBrandId]);

  // Auto-load inventory from URL params on initial load
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const warehouseParam = searchParams.get('warehouse');
    const dateParam = searchParams.get('date');
    const brandIdParam = searchParams.get('brand_id');

    // If we have step=2 and required params, auto-load the inventory.
    // Guard with a per-combo signature ref so a warehouse that genuinely has
    // 0 calculated items doesn't re-trigger this effect forever (the
    // `inventoryItems.length === 0` condition stays true for empty warehouses,
    // which previously looped and re-forced step 2 — trapping the user).
    if (stepParam === '2' && warehouseParam && dateParam && selectedBrandId &&
        parseInt(brandIdParam, 10) === selectedBrandId && warehouses.length > 0 &&
        inventoryItems.length === 0 && !loading) {
      const signature = `${warehouseParam}|${dateParam}|${selectedBrandId}`;
      // Already auto-loaded (or attempted) this exact combo — don't re-fire.
      if (autoLoadSignatureRef.current !== signature) {
        // Verify warehouse exists
        const warehouseExists = warehouses.some(w => w.code === warehouseParam);
        if (warehouseExists) {
          loadInventoryForWarehouse();
        }
      }
    }
  }, [searchParams, selectedBrandId, warehouses, inventoryItems.length, loading]);

  useEffect(() => {
    // Filter items based on search
    if (searchTerm) {
      const filtered = inventoryItems.filter(item =>
        item.primary_sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(inventoryItems);
    }
    setPage(0);
  }, [searchTerm, inventoryItems]);

  const loadWarehouses = async () => {
    try {
      const res = await getWarehouses({ is_active: true });
      setWarehouses(res?.warehouses || []);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const loadPastCounts = async () => {
    try {
      const res = await api.get(`${API_BASE}/api/physical-counts`, {
        params: { brand_id: selectedBrandId, limit: 20 }
      });
      // API returns { success, counts, total, ... } — read `counts` (was reading
      // the non-existent `data` key, so Recent Counts always showed 0).
      setPastCounts(res.data?.counts || res.data?.data || []);
    } catch (err) {
      console.error('Failed to load past counts:', err);
    }
  };

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
      // Update URL with brand_id
      updateUrlParams({ brand_id: brandId.toString() });
    }
  };

  const handleWarehouseChange = (event) => {
    const warehouse = event.target.value;
    setSelectedWarehouse(warehouse);
    updateUrlParams({ warehouse });
  };

  const handleDateChange = (event) => {
    const date = event.target.value;
    setCountDate(date);
    updateUrlParams({ date });
  };

  const loadInventoryForWarehouse = async () => {
    if (!selectedBrandId) {
      setError('Please select a brand');
      return;
    }
    if (!selectedWarehouse || !countDate) {
      setError('Please select a warehouse and count date');
      return;
    }

    // Mark this brand|warehouse|date combo as loaded so the URL-driven
    // auto-load effect won't re-fire for it (critical for 0-item warehouses).
    autoLoadSignatureRef.current = `${selectedWarehouse}|${countDate}|${selectedBrandId}`;

    setLoading(true);
    setError(null);

    try {
      // Get point-in-time inventory for the selected warehouse and date
      const res = await getCalculatedStockLevels({
        brand_id: selectedBrandId,
        warehouse_code: selectedWarehouse,
        as_of_date: countDate,
        include_zero: false,
        limit: 2000
      });

      const items = res?.data?.items || [];
      setInventoryItems(items);
      setFilteredItems(items);
      batchGenRef.current += 1; // reads still in flight belong to the previous count
      setSkuBatches({}); // Clear batch cache for fresh data with new parameters
      setSkuBatchErrors({});
      setSkuBatchLoading({});
      setExpandedSku(null); // Reset expanded state
      setSystemBatchEdits({}); // Clear system batch edits
      setEditingSystemBatch(null); // Clear editing state

      // Initialize counted data with system quantities
      const initialCounted = {};
      items.forEach(item => {
        initialCounted[item.primary_sku] = {
          systemQty: item.total_quantity || 0,
          countedQty: null, // null means not yet counted
          batches: []
        };
      });
      setCountedData(initialCounted);

      // Move to step 2 and update URL with all params
      setCurrentStep(2);
      updateUrlParams({
        brand_id: selectedBrandId.toString(),
        warehouse: selectedWarehouse,
        date: countDate,
        step: '2'
      });
      setSuccess(`Loaded ${items.length} items for ${selectedBrand?.name || 'Brand'} - ${selectedWarehouse} as of ${countDate}`);
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setError('Failed to load inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchesForSku = async (sku) => {
    if (skuBatches[sku] || skuBatchLoading[sku]) return; // Already loaded or loading

    const gen = batchGenRef.current;
    setSkuBatchLoading(prev => ({ ...prev, [sku]: true }));
    try {
      const res = await getSkuBatches(sku, {
        warehouse_code: selectedWarehouse,
        as_of_date: countDate,
        brand_id: selectedBrandId,
        include_depleted: true  // Show all historical batches for physical count verification
      });
      if (gen !== batchGenRef.current) return; // a reload happened: this answer is for the previous count
      setSkuBatches(prev => ({
        ...prev,
        [sku]: res?.data?.batches || []
      }));
      setSkuBatchErrors(prev => ({ ...prev, [sku]: null }));
    } catch (err) {
      if (gen !== batchGenRef.current) return;
      console.error('Failed to load batches for SKU:', err);
      if (err?.response?.data?.code === 'PAST_DATE_STOCK_UNAVAILABLE') {
        setSkuBatchErrors(prev => ({ ...prev, [sku]: err.response.data.error || 'Stock for this date could not be read right now. Please try again.' }));
      }
    } finally {
      // The reload already reset the loading flags; only the current count's read clears its own.
      if (gen === batchGenRef.current) setSkuBatchLoading(prev => ({ ...prev, [sku]: false }));
    }
  };

  const handleExpandSku = (sku) => {
    if (expandedSku === sku) {
      setExpandedSku(null);
    } else {
      setExpandedSku(sku);
      loadBatchesForSku(sku);
    }
  };

  const handleCountedQtyChange = (sku, value) => {
    const qty = value === '' ? null : parseInt(value, 10);
    setCountedData(prev => ({
      ...prev,
      [sku]: {
        ...prev[sku],
        countedQty: qty
      }
    }));
  };

  const handleVarianceReasonChange = (sku, value) => {
    setCountedData(prev => ({
      ...prev,
      [sku]: {
        ...prev[sku],
        varianceReason: value
      }
    }));
  };

  const handleAddBatch = () => {
    if (!currentSkuForBatch || !newBatch.quantity) return;

    setCountedData(prev => {
      const existing = prev[currentSkuForBatch] || { systemQty: 0, countedQty: null, batches: [] };
      const newBatches = [...existing.batches, { ...newBatch, quantity: parseInt(newBatch.quantity, 10) }];
      const totalFromBatches = newBatches.reduce((sum, b) => sum + (b.quantity || 0), 0);

      return {
        ...prev,
        [currentSkuForBatch]: {
          ...existing,
          batches: newBatches,
          countedQty: totalFromBatches
        }
      };
    });

    setNewBatch({ batch_number: '', expiry_date: '', quantity: '' });
    setAddBatchDialogOpen(false);
    setCurrentSkuForBatch(null);
  };

  const handleRemoveBatch = (sku, batchIndex) => {
    setCountedData(prev => {
      const existing = prev[sku] || { systemQty: 0, countedQty: null, batches: [] };
      const newBatches = existing.batches.filter((_, idx) => idx !== batchIndex);
      const totalFromBatches = newBatches.reduce((sum, b) => sum + (b.quantity || 0), 0);

      return {
        ...prev,
        [sku]: {
          ...existing,
          batches: newBatches,
          countedQty: newBatches.length > 0 ? totalFromBatches : existing.countedQty
        }
      };
    });
  };

  // Handle starting edit mode for a system batch
  const handleStartEditSystemBatch = (sku, batchIndex, currentQty, batch) => {
    setEditingSystemBatch({ sku, batchIndex });
    // Initialize the edit value if not already set
    if (!systemBatchEdits[sku]?.[batchIndex]) {
      // Get the last batch's COGS as default if this batch has no COGS
      const batches = skuBatches[sku] || [];
      const lastBatchWithCogs = [...batches].reverse().find(b => parseFloat(b.unit_cost) > 0);
      const defaultCogs = batch?.unit_cost || lastBatchWithCogs?.unit_cost || 0;

      setSystemBatchEdits(prev => ({
        ...prev,
        [sku]: {
          ...(prev[sku] || {}),
          [batchIndex]: {
            editedQty: currentQty,
            varianceRemark: '',
            editedBatchName: batch?.batch_number || '',
            editedExpiry: batch?.expiry_date && batch.expiry_date !== '1970-01-01' ? batch.expiry_date : '',
            editedCogs: parseFloat(defaultCogs) || 0
          }
        }
      }));
    }
  };

  // Handle saving system batch edit
  const handleSaveSystemBatchEdit = () => {
    setEditingSystemBatch(null);
  };

  // Handle canceling system batch edit
  const handleCancelSystemBatchEdit = (sku, batchIndex) => {
    setEditingSystemBatch(null);
    // Remove the edit if it was just started and no changes made
    setSystemBatchEdits(prev => {
      const skuEdits = { ...(prev[sku] || {}) };
      delete skuEdits[batchIndex];
      if (Object.keys(skuEdits).length === 0) {
        const newEdits = { ...prev };
        delete newEdits[sku];
        return newEdits;
      }
      return { ...prev, [sku]: skuEdits };
    });
  };

  // Handle system batch quantity change
  const handleSystemBatchQtyChange = (sku, batchIndex, value) => {
    const qty = value === '' ? 0 : parseInt(value, 10);
    setSystemBatchEdits(prev => ({
      ...prev,
      [sku]: {
        ...(prev[sku] || {}),
        [batchIndex]: {
          ...(prev[sku]?.[batchIndex] || {}),
          editedQty: qty
        }
      }
    }));
  };

  // Handle system batch variance remark change
  const handleSystemBatchRemarkChange = (sku, batchIndex, value) => {
    setSystemBatchEdits(prev => ({
      ...prev,
      [sku]: {
        ...(prev[sku] || {}),
        [batchIndex]: {
          ...(prev[sku]?.[batchIndex] || {}),
          varianceRemark: value
        }
      }
    }));
  };

  // Handle system batch name change
  const handleSystemBatchNameChange = (sku, batchIndex, value) => {
    setSystemBatchEdits(prev => ({
      ...prev,
      [sku]: {
        ...(prev[sku] || {}),
        [batchIndex]: {
          ...(prev[sku]?.[batchIndex] || {}),
          editedBatchName: value
        }
      }
    }));
  };

  // Handle system batch expiry change
  const handleSystemBatchExpiryChange = (sku, batchIndex, value) => {
    setSystemBatchEdits(prev => ({
      ...prev,
      [sku]: {
        ...(prev[sku] || {}),
        [batchIndex]: {
          ...(prev[sku]?.[batchIndex] || {}),
          editedExpiry: value
        }
      }
    }));
  };

  // Handle system batch COGS change
  const handleSystemBatchCogsChange = (sku, batchIndex, value) => {
    const cogs = value === '' ? 0 : parseFloat(value);
    setSystemBatchEdits(prev => ({
      ...prev,
      [sku]: {
        ...(prev[sku] || {}),
        [batchIndex]: {
          ...(prev[sku]?.[batchIndex] || {}),
          editedCogs: cogs
        }
      }
    }));
  };

  // Get the effective quantity for a system batch (edited or original)
  const getSystemBatchQty = (sku, batchIndex, originalQty) => {
    const edit = systemBatchEdits[sku]?.[batchIndex];
    return edit !== undefined ? edit.editedQty : originalQty;
  };

  // Check if a system batch has been edited
  const isSystemBatchEdited = (sku, batchIndex) => {
    return systemBatchEdits[sku]?.[batchIndex] !== undefined;
  };

  // Check if SKU has any system batch edits
  const hasSystemBatchEdits = (sku) => {
    return systemBatchEdits[sku] && Object.keys(systemBatchEdits[sku]).length > 0;
  };

  // Get the total counted quantity for a SKU from system batch edits
  const getSystemBatchEditTotal = (sku) => {
    const batches = skuBatches[sku] || [];
    if (batches.length === 0) return null;

    // If no edits for this SKU, return null (not counted via batch editing)
    if (!hasSystemBatchEdits(sku)) return null;

    // Calculate total from all batches (using edited value if available, original otherwise)
    return batches.reduce((sum, batch, idx) => {
      const originalQty = parseFloat(batch.current_quantity) || 0;
      const editedQty = systemBatchEdits[sku]?.[idx]?.editedQty;
      return sum + (editedQty !== undefined ? editedQty : originalQty);
    }, 0);
  };

  const getVariance = (sku) => {
    const data = countedData[sku];

    // First check if there are system batch edits
    const batchEditTotal = getSystemBatchEditTotal(sku);
    if (batchEditTotal !== null && data) {
      return batchEditTotal - data.systemQty;
    }

    // Otherwise use the standard counted quantity
    if (!data || data.countedQty === null) return null;
    return data.countedQty - data.systemQty;
  };

  const getVarianceColor = (variance) => {
    if (variance === null) return 'default';
    if (variance === 0) return 'success';
    if (Math.abs(variance) <= 5) return 'warning';
    return 'error';
  };

  const getCountedItemsCount = () => {
    // Count SKUs that have either a countedQty or system batch edits
    return Object.keys(countedData).filter(sku => {
      const data = countedData[sku];
      return data.countedQty !== null || hasSystemBatchEdits(sku);
    }).length;
  };

  const getTotalVariance = () => {
    return Object.entries(countedData).reduce((sum, [sku, data]) => {
      const variance = getVariance(sku);
      return sum + (variance || 0);
    }, 0);
  };

  const getDiscrepancyCount = () => {
    return Object.entries(countedData).filter(([sku, data]) => {
      const variance = getVariance(sku);
      return variance !== null && variance !== 0;
    }).length;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setImportFile(file);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setImportFile(null);
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    setImporting(true);
    setError(null);
    setWarning(null);
    setSuccess(null);   // DT-901: an earlier import's "Imported N" must not stay up beside an all-refused result

    try {
      // Parse the CSV client-side and submit pre-mapped rows. The /import
      // endpoint expects a `rows` array (not a raw file upload) — posting the
      // file here previously failed with "rows array is required". Warehouse /
      // count_date fall back to the ones selected for this count when the CSV
      // omits them, so a simple sku,counted_quantity file works too.
      const text = await importFile.text();
      const allLines = text.split(/\r?\n/).filter(l => l.trim());
      if (allLines.length < 2) {
        setError('CSV is empty or has no data rows.');
        setImporting(false);
        return;
      }
      const header = allLines[0].split(',').map(h => h.trim().toLowerCase());
      const col = (name) => header.indexOf(name);
      const rows = allLines.slice(1).map((line) => {
        const v = line.split(',');
        const get = (name) => { const i = col(name); return i >= 0 ? String(v[i] || '').trim() : ''; };
        return {
          sku: get('sku') || get('our_sku'),
          counted_quantity: parseFloat(get('counted_quantity') || get('counted_qty') || get('quantity')) || 0,
          warehouse_code: (get('warehouse_code') || selectedWarehouse || '').toUpperCase().trim(),
          count_date: get('count_date') || countDate,
          batch_number: get('batch_number') || null,
          gtin: get('gtin') || null,
          expiry_date: get('expiry_date') || null,
        };
      }).filter((r) => r.sku && r.warehouse_code && r.count_date && !isNaN(r.counted_quantity) && r.counted_quantity >= 0);

      if (rows.length === 0) {
        setError('No valid rows found — the CSV needs at least an sku and counted_quantity column.');
        setImporting(false);
        return;
      }

      const res = await api.post(`${API_BASE}/api/physical-counts/import`, {
        rows,
        source: 'csv_import',
        original_filename: importFile.name,
      });
      const r = res.data || {};
      const refused = Array.isArray(r.refused) ? r.refused : [];
      if ((r.countsCreated || 0) > 0 || refused.length === 0) {
        setSuccess(
          `Imported ${r.countsCreated || 0} count(s), ${r.itemsCreated || 0} item(s)` +
          (r.adjustmentsCreated ? `, ${r.adjustmentsCreated} adjustment(s)` : '') + ' from CSV'
        );
      }
      // Warehouse/date groups the server refused (DT-864): say which and why, not only what was imported.
      setWarning(refused.length
        ? `Not imported: ${refused.map((g) => `${g.warehouse_code} ${g.count_date} (${g.error})`).join('; ')}`
        : null);
      setImportDialogOpen(false);
      setImportFile(null);
      // Refresh history so the newly-created count shows.
      if (typeof loadPastCounts === 'function') loadPastCounts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmitCount = async () => {
    // Build items to submit - include both direct counted quantities and system batch edits
    const itemsToSubmit = Object.entries(countedData)
      .filter(([sku, data]) => {
        // Include if has direct counted quantity OR has system batch edits
        return data.countedQty !== null || hasSystemBatchEdits(sku);
      })
      .map(([sku, data]) => {
        // Calculate counted quantity - prefer system batch edit total if available
        const batchEditTotal = getSystemBatchEditTotal(sku);
        const countedQty = batchEditTotal !== null ? batchEditTotal : data.countedQty;
        const variance = countedQty - data.systemQty;

        // Build batch details including variance remarks from system batch edits
        let batchDetails = data.batches || [];
        const systemBatchRemarks = [];

        // If there are system batch edits, build batchDetails from the edited batches
        // This ensures the backend has the correct batch_number and expiry_date
        if (hasSystemBatchEdits(sku)) {
          const batches = skuBatches[sku] || [];
          const editedBatchDetails = [];

          batches.forEach((batch, idx) => {
            const edit = systemBatchEdits[sku]?.[idx];
            if (edit) {
              const originalQty = parseFloat(batch.current_quantity) || 0;
              const editedQty = edit.editedQty;
              const batchVariance = editedQty - originalQty;

              // Use edited batch name/expiry if provided, otherwise use original
              const finalBatchName = edit.editedBatchName !== undefined ? edit.editedBatchName : batch.batch_number;
              const finalExpiry = edit.editedExpiry !== undefined ? edit.editedExpiry : batch.expiry_date;

              // Use edited COGS if provided, otherwise use original
              const originalCogs = parseFloat(batch.unit_cost) || 0;
              const finalCogs = edit.editedCogs !== undefined ? edit.editedCogs : originalCogs;

              // Add to edited batch details with actual batch info (include even if no qty variance, but batch name/expiry/cogs changed)
              const hasNameChange = edit.editedBatchName !== undefined && edit.editedBatchName !== batch.batch_number;
              const hasExpiryChange = edit.editedExpiry !== undefined && edit.editedExpiry !== (batch.expiry_date || '');
              const hasCogsChange = edit.editedCogs !== undefined && edit.editedCogs !== originalCogs;
              const hasQtyChange = batchVariance !== 0;

              if (hasQtyChange || hasNameChange || hasExpiryChange || hasCogsChange) {
                editedBatchDetails.push({
                  batch_number: finalBatchName,
                  original_batch_number: batch.batch_number, // Keep original for reference
                  expiry_date: finalExpiry,
                  original_expiry_date: batch.expiry_date, // Keep original for reference
                  quantity: editedQty,
                  original_quantity: originalQty,
                  variance: batchVariance,
                  batch_id: batch.batch_id || batch.id, // Include batch ID for updates
                  unit_cost: finalCogs, // Include COGS for saving
                  original_unit_cost: originalCogs // Keep original for reference
                });

                if (edit.varianceRemark) {
                  systemBatchRemarks.push(`${finalBatchName || `Batch ${idx + 1}`}: ${edit.varianceRemark} (${batchVariance > 0 ? '+' : ''}${batchVariance})`);
                }
              }
            }
          });

          // Use edited batch details if we have any, otherwise keep the original batchDetails
          if (editedBatchDetails.length > 0) {
            batchDetails = editedBatchDetails;
          }
        }

        // Combine variance reasons
        const allRemarks = [
          data.varianceReason || '',
          ...systemBatchRemarks
        ].filter(r => r).join('; ');

        return {
          sku,
          system_quantity: data.systemQty,
          counted_quantity: countedQty,
          variance: variance,
          batches: batchDetails,
          variance_reason: allRemarks,
          system_batch_edits: systemBatchEdits[sku] || null // Include the raw batch edits for backend processing
        };
      });

    if (itemsToSubmit.length === 0) {
      setError('Please count at least one item before submitting');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post(`${API_BASE}/api/physical-counts/submit`, {
        brand_id: selectedBrandId,
        warehouse_code: selectedWarehouse,
        count_date: countDate,
        notes,
        items: itemsToSubmit
      });

      setSuccess('Physical count submitted successfully! Inventory adjustments have been applied.');
      setCurrentStep(1);
      setCountedData({});
      setSystemBatchEdits({});
      setEditingSystemBatch(null);
      setInventoryItems([]);
      setSelectedWarehouse('');
      setNotes('');
      // Clear URL params after submission
      updateUrlParams({ step: null, warehouse: null, date: null });
      loadPastCounts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit physical count');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['sku', 'counted_quantity', 'batch_number', 'expiry_date'];
    // Use obvious placeholder SKUs (NOT real catalogue SKUs) so that importing
    // the template unedited can never create phantom counts on real products.
    const sampleData = [
      ['EXAMPLE-SKU-001', '100', 'BATCH-EXAMPLE-001', '2025-06-30'],
      ['EXAMPLE-SKU-001', '50', 'BATCH-EXAMPLE-002', '2025-09-15'],
      ['EXAMPLE-SKU-002', '200', '', '']
    ];

    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `physical_count_template_${selectedWarehouse}_${countDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Render Step 1: Setup
  const renderSetup = () => (
    <Box>
      <Grid container spacing={3}>
        {/* Setup Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CountIcon color="primary" />
                New Physical Count
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Select brand, warehouse and count date to begin. The system will load calculated
                inventory levels as of the selected date.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Brand Dropdown */}
                <FormControl fullWidth required>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    value={selectedBrandId || ''}
                    label="Brand"
                    onChange={handleBrandChange}
                  >
                    {brands.length === 0 ? (
                      <MenuItem disabled>No brands available</MenuItem>
                    ) : (
                      excludeCompanyLevel(brands).map(brand => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Warehouse</InputLabel>
                  <Select
                    value={selectedWarehouse}
                    label="Warehouse"
                    onChange={handleWarehouseChange}
                    startAdornment={
                      <InputAdornment position="start">
                        <WarehouseIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {warehouses.length === 0 ? (
                      <MenuItem disabled>No warehouses available</MenuItem>
                    ) : (
                      warehouses.map(w => (
                        <MenuItem key={w.id} value={w.code}>
                          {w.name} ({w.code})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label="Count Date"
                  type="date"
                  value={countDate}
                  onChange={handleDateChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '2000-01-01' }}
                  helperText="When was the physical count performed?"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  label="Notes (Optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  placeholder="e.g., Monthly inventory count, Year-end audit"
                />

                <Button
                  variant="contained"
                  size="large"
                  onClick={loadInventoryForWarehouse}
                  disabled={!selectedBrandId || !selectedWarehouse || !countDate || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <InventoryIcon />}
                  fullWidth
                >
                  {loading ? 'Loading Calculated Inventory...' : 'Load Calculated Inventory & Start Count'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Import
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Already have count data in a spreadsheet? Import it directly.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium">CSV Format:</Typography>
                <Typography variant="body2">
                  sku, counted_quantity, batch_number (optional), expiry_date (optional)
                </Typography>
              </Alert>

              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => { setError(null); setImportDialogOpen(true); }}
                fullWidth
                disabled={!selectedBrandId || !selectedWarehouse || !countDate}
              >
                Import from CSV
              </Button>

              {(!selectedBrandId || !selectedWarehouse || !countDate) && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Select brand, warehouse and date first
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Past Counts */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setShowPastCounts(!showPastCounts)}
              >
                <Typography variant="h6">
                  Recent Counts ({pastCounts.length})
                </Typography>
                <IconButton size="small">
                  {showPastCounts ? <ArrowUpIcon /> : <ArrowDownIcon />}
                </IconButton>
              </Box>

              <Collapse in={showPastCounts}>
                {pastCounts.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    No previous counts found
                  </Typography>
                ) : (
                  <Table size="small" sx={{ mt: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Warehouse</TableCell>
                        <TableCell align="right">Items</TableCell>
                        <TableCell align="right">Variance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pastCounts.slice(0, 5).map((count) => (
                        <TableRow key={count.id} hover>
                          <TableCell>{formatDate(count.count_date)}</TableCell>
                          <TableCell>
                            <Chip label={count.warehouse_code} size="small" />
                          </TableCell>
                          <TableCell align="right">{count.total_items}</TableCell>
                          <TableCell align="right">
                            <Typography
                              color={count.total_variance === 0 ? 'success.main' : 'error.main'}
                            >
                              {count.total_variance > 0 ? '+' : ''}{count.total_variance}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Collapse>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // Render Step 2: Count Items
  const renderCounting = () => (
    <Box>
      {/* Header with summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="h6">
                {selectedBrand?.name || 'Brand'} - {selectedWarehouse}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Count Date: {countDate} | Showing calculated system quantities
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="body2" color="textSecondary">Total Items</Typography>
              <Typography variant="h5" fontWeight="bold">{inventoryItems.length}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="body2" color="textSecondary">Counted</Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {getCountedItemsCount()}
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="body2" color="textSecondary">Discrepancies</Typography>
              <Typography variant="h5" fontWeight="bold" color={getDiscrepancyCount() > 0 ? 'error.main' : 'success.main'}>
                {getDiscrepancyCount()}
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="body2" color="textSecondary">Net Variance</Typography>
              <Typography variant="h5" fontWeight="bold" color={getTotalVariance() !== 0 ? 'error.main' : 'success.main'}>
                {getTotalVariance() > 0 ? '+' : ''}{getTotalVariance()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by SKU or product name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => { setError(null); setImportDialogOpen(true); }}
        >
          Import CSV
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadTemplate}
        >
          Download Template
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          onClick={() => {
            setCurrentStep(1);
            setInventoryItems([]);
            setCountedData({});
            setSystemBatchEdits({});
            setEditingSystemBatch(null);
            autoLoadSignatureRef.current = null;
            // Clear step/warehouse/date from the URL so the auto-load effect
            // has nothing to act on (prevents the cancel from being undone by
            // a stale step=2 deep-link param).
            updateUrlParams({ step: '1', warehouse: '', date: '' });
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CompleteIcon />}
          onClick={() => {
            setCurrentStep(3);
            updateUrlParams({ step: '3' });
          }}
          disabled={getCountedItemsCount() === 0}
        >
          Review & Submit ({getCountedItemsCount()} items)
        </Button>
      </Box>

      {/* Items table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell width={50}></TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell align="right">System Qty</TableCell>
                <TableCell align="center" sx={{ minWidth: 150 }}>Counted Qty</TableCell>
                <TableCell align="center">Variance</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Reason (if variance)</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => {
                  const sku = item.primary_sku;
                  const data = countedData[sku] || { systemQty: 0, countedQty: null, batches: [] };
                  const variance = getVariance(sku);
                  const isExpanded = expandedSku === sku;
                  const existingBatches = skuBatches[sku] || [];

                  return (
                    <React.Fragment key={sku}>
                      <TableRow
                        hover
                        sx={{
                          backgroundColor: variance !== null && variance !== 0
                            ? variance > 0 ? 'success.lighter' : 'error.lighter'
                            : 'inherit'
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleExpandSku(sku)}
                          >
                            {isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{sku}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                            {item.product_name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="medium">
                            {formatNumber(data.systemQty)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={data.countedQty === null ? '' : data.countedQty}
                            onChange={(e) => handleCountedQtyChange(sku, e.target.value)}
                            placeholder="Enter qty"
                            sx={{ width: 120 }}
                            inputProps={{ min: 0, style: { textAlign: 'center' } }}
                            disabled={data.batches.length > 0}
                          />
                          {data.batches.length > 0 && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              (from {data.batches.length} batches)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {variance !== null ? (
                            <Chip
                              icon={variance !== 0 ? <DiscrepancyIcon sx={{ fontSize: 16 }} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                              label={variance === 0 ? 'OK' : (variance > 0 ? `+${variance}` : variance)}
                              size="small"
                              color={getVarianceColor(variance)}
                            />
                          ) : (
                            <Typography color="textSecondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {variance !== null && variance !== 0 ? (
                            <TextField
                              size="small"
                              placeholder="e.g., Damaged, Lost, Found"
                              value={data.varianceReason || ''}
                              onChange={(e) => handleVarianceReasonChange(sku, e.target.value)}
                              sx={{ width: '100%', minWidth: 150 }}
                            />
                          ) : (
                            <Typography color="textSecondary" variant="body2">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Add batch details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setCurrentSkuForBatch(sku);
                                setAddBatchDialogOpen(true);
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* Expanded row showing batch details */}
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                              <Grid container spacing={3}>
                                {/* System batches */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    System Batches (as of {countDate})
                                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                      Click edit to adjust counted qty
                                    </Typography>
                                  </Typography>
                                  {skuBatchLoading[sku] ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                                      <CircularProgress size={16} />
                                      <Typography variant="body2" color="text.secondary">
                                        Loading batch details...
                                      </Typography>
                                    </Box>
                                  ) : skuBatchErrors[sku] ? (
                                    <Alert
                                      severity="warning"
                                      action={
                                        <Button color="inherit" size="small" onClick={() => loadBatchesForSku(sku)}>
                                          Retry
                                        </Button>
                                      }
                                    >
                                      {skuBatchErrors[sku]}
                                    </Alert>
                                  ) : existingBatches.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                      No batch details in system
                                    </Typography>
                                  ) : (
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Batch #</TableCell>
                                          <TableCell>Expiry</TableCell>
                                          <TableCell align="right">COGS</TableCell>
                                          <TableCell align="right">System Qty</TableCell>
                                          <TableCell align="right">Counted Qty</TableCell>
                                          <TableCell width={80}>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {existingBatches.map((batch, idx) => {
                                          const isEditing = editingSystemBatch?.sku === sku && editingSystemBatch?.batchIndex === idx;
                                          const isEdited = isSystemBatchEdited(sku, idx);
                                          const editData = systemBatchEdits[sku]?.[idx];
                                          const originalQty = parseFloat(batch.current_quantity) || 0;
                                          const countedQty = isEdited ? editData.editedQty : originalQty;
                                          const batchVariance = countedQty - originalQty;

                                          // Get edited values or originals
                                          const displayBatchName = isEdited && editData?.editedBatchName !== undefined
                                            ? editData.editedBatchName
                                            : (batch.batch_number || '');
                                          const displayExpiry = isEdited && editData?.editedExpiry !== undefined
                                            ? editData.editedExpiry
                                            : (batch.expiry_date && batch.expiry_date !== '1970-01-01' ? batch.expiry_date : '');
                                          const originalCogs = parseFloat(batch.unit_cost) || 0;
                                          const displayCogs = isEdited && editData?.editedCogs !== undefined
                                            ? editData.editedCogs
                                            : originalCogs;

                                          return (
                                            <React.Fragment key={idx}>
                                              <TableRow sx={{ backgroundColor: isEdited && batchVariance !== 0 ? (batchVariance > 0 ? 'success.lighter' : 'error.lighter') : 'inherit' }}>
                                                <TableCell>
                                                  {isEditing ? (
                                                    <TextField
                                                      size="small"
                                                      value={displayBatchName}
                                                      onChange={(e) => handleSystemBatchNameChange(sku, idx, e.target.value)}
                                                      placeholder="Batch name"
                                                      sx={{ width: 180 }}
                                                    />
                                                  ) : (
                                                    <Typography
                                                      fontWeight={isEdited && editData?.editedBatchName !== batch.batch_number ? 'bold' : 'normal'}
                                                      color={isEdited && editData?.editedBatchName !== batch.batch_number ? 'primary.main' : 'inherit'}
                                                    >
                                                      {displayBatchName || '—'}
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {isEditing ? (
                                                    <TextField
                                                      type="date"
                                                      size="small"
                                                      value={displayExpiry}
                                                      onChange={(e) => handleSystemBatchExpiryChange(sku, idx, e.target.value)}
                                                      sx={{ width: 140 }}
                                                      InputLabelProps={{ shrink: true }}
                                                    />
                                                  ) : (
                                                    <Typography
                                                      fontWeight={isEdited && editData?.editedExpiry !== (batch.expiry_date || '') ? 'bold' : 'normal'}
                                                      color={isEdited && editData?.editedExpiry !== (batch.expiry_date || '') ? 'primary.main' : 'inherit'}
                                                    >
                                                      {displayExpiry ? formatDate(displayExpiry) : '—'}
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell align="right">
                                                  {isEditing ? (
                                                    <TextField
                                                      type="number"
                                                      size="small"
                                                      value={displayCogs}
                                                      onChange={(e) => handleSystemBatchCogsChange(sku, idx, e.target.value)}
                                                      sx={{ width: 80 }}
                                                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                                                    />
                                                  ) : (
                                                    <Typography
                                                      fontWeight={isEdited && editData?.editedCogs !== originalCogs ? 'bold' : 'normal'}
                                                      color={isEdited && editData?.editedCogs !== originalCogs ? 'primary.main' : 'inherit'}
                                                    >
                                                      {displayCogs > 0 ? formatCurrency(displayCogs) : '—'}
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell align="right">{formatNumber(originalQty)}</TableCell>
                                                <TableCell align="right">
                                                  {isEditing ? (
                                                    <TextField
                                                      type="number"
                                                      size="small"
                                                      value={countedQty}
                                                      onChange={(e) => handleSystemBatchQtyChange(sku, idx, e.target.value)}
                                                      sx={{ width: 80 }}
                                                      inputProps={{ min: 0, style: { textAlign: 'right' } }}
                                                    />
                                                  ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                      <Typography
                                                        fontWeight={isEdited ? 'bold' : 'normal'}
                                                        color={isEdited && batchVariance !== 0 ? (batchVariance > 0 ? 'success.main' : 'error.main') : 'inherit'}
                                                      >
                                                        {formatNumber(countedQty)}
                                                      </Typography>
                                                      {isEdited && batchVariance !== 0 && (
                                                        <Chip
                                                          label={batchVariance > 0 ? `+${batchVariance}` : batchVariance}
                                                          size="small"
                                                          color={batchVariance > 0 ? 'success' : 'error'}
                                                          sx={{ ml: 0.5, height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                                                        />
                                                      )}
                                                    </Box>
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {isEditing ? (
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                      <IconButton size="small" onClick={handleSaveSystemBatchEdit} color="success">
                                                        <CheckIcon fontSize="small" />
                                                      </IconButton>
                                                      <IconButton size="small" onClick={() => handleCancelSystemBatchEdit(sku, idx)} color="error">
                                                        <CloseIcon fontSize="small" />
                                                      </IconButton>
                                                    </Box>
                                                  ) : (
                                                    <Tooltip title="Edit batch details">
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleStartEditSystemBatch(sku, idx, originalQty, batch)}
                                                      >
                                                        <EditIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                              {/* Variance remark row - shown when batch is edited and has variance */}
                                              {isEdited && batchVariance !== 0 && (
                                                <TableRow>
                                                  <TableCell colSpan={5} sx={{ pt: 0, pb: 1, borderBottom: 'none' }}>
                                                    <TextField
                                                      size="small"
                                                      placeholder="Variance reason (e.g., Damaged, Lost, Found extra)"
                                                      value={editData?.varianceRemark || ''}
                                                      onChange={(e) => handleSystemBatchRemarkChange(sku, idx, e.target.value)}
                                                      fullWidth
                                                      sx={{ mt: 0.5 }}
                                                      InputProps={{
                                                        startAdornment: (
                                                          <InputAdornment position="start">
                                                            <DiscrepancyIcon fontSize="small" color="warning" />
                                                          </InputAdornment>
                                                        )
                                                      }}
                                                    />
                                                  </TableCell>
                                                </TableRow>
                                              )}
                                            </React.Fragment>
                                          );
                                        })}
                                        {/* Total row */}
                                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                          <TableCell colSpan={3}><Typography fontWeight="bold">Total</Typography></TableCell>
                                          <TableCell align="right">
                                            <Typography fontWeight="bold">
                                              {formatNumber(existingBatches.reduce((sum, b) => sum + (parseFloat(b.current_quantity) || 0), 0))}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography fontWeight="bold">
                                              {formatNumber(existingBatches.reduce((sum, b, idx) => {
                                                const isEdited = isSystemBatchEdited(sku, idx);
                                                const originalQty = parseFloat(b.current_quantity) || 0;
                                                return sum + (isEdited ? systemBatchEdits[sku]?.[idx]?.editedQty || 0 : originalQty);
                                              }, 0))}
                                            </Typography>
                                          </TableCell>
                                          <TableCell></TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  )}
                                </Grid>

                                {/* Counted batches */}
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">
                                      Counted Batches
                                    </Typography>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => {
                                        setCurrentSkuForBatch(sku);
                                        setAddBatchDialogOpen(true);
                                      }}
                                    >
                                      Add Batch
                                    </Button>
                                  </Box>
                                  {data.batches.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                      No batches entered. Enter total quantity above or add batch details.
                                    </Typography>
                                  ) : (
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Batch #</TableCell>
                                          <TableCell>Expiry</TableCell>
                                          <TableCell align="right">Qty</TableCell>
                                          <TableCell width={50}></TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {data.batches.map((batch, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{batch.batch_number || '—'}</TableCell>
                                            <TableCell>{batch.expiry_date || '—'}</TableCell>
                                            <TableCell align="right">{formatNumber(batch.quantity)}</TableCell>
                                            <TableCell>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleRemoveBatch(sku, idx)}
                                              >
                                                <CloseIcon fontSize="small" />
                                              </IconButton>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                        <TableRow>
                                          <TableCell colSpan={2}>
                                            <Typography fontWeight="bold">Total</Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography fontWeight="bold">
                                              {formatNumber(data.batches.reduce((sum, b) => sum + (b.quantity || 0), 0))}
                                            </Typography>
                                          </TableCell>
                                          <TableCell></TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  )}
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 250]}
        />
      </Card>
    </Box>
  );

  // Render Step 3: Review & Submit
  const renderReview = () => {
    // Include items with direct counts OR system batch edits
    const itemsWithVariance = Object.entries(countedData)
      .filter(([sku, data]) => data.countedQty !== null || hasSystemBatchEdits(sku))
      .map(([sku, data]) => {
        // Calculate counted quantity - prefer system batch edit total if available
        const batchEditTotal = getSystemBatchEditTotal(sku);
        const countedQty = batchEditTotal !== null ? batchEditTotal : data.countedQty;

        return {
          sku,
          ...data,
          countedQty: countedQty,
          variance: countedQty - data.systemQty
        };
      })
      .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));

    const discrepancies = itemsWithVariance.filter(i => i.variance !== 0);
    const matches = itemsWithVariance.filter(i => i.variance === 0);

    return (
      <Box>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Review Physical Count
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Review the count results below. Submitting will create backdated inventory adjustments
              for items with discrepancies.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.100' }}>
                  <Typography variant="body2" color="textSecondary">Warehouse</Typography>
                  <Typography variant="h6">{selectedWarehouse}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.100' }}>
                  <Typography variant="body2" color="textSecondary">Count Date</Typography>
                  <Typography variant="h6">{countDate}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'success.lighter' }}>
                  <Typography variant="body2" color="textSecondary">Items Match</Typography>
                  <Typography variant="h6" color="success.main">{matches.length}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: discrepancies.length > 0 ? 'error.lighter' : 'success.lighter' }}>
                  <Typography variant="body2" color="textSecondary">Discrepancies</Typography>
                  <Typography variant="h6" color={discrepancies.length > 0 ? 'error.main' : 'success.main'}>
                    {discrepancies.length}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Discrepancies */}
        {discrepancies.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" color="error" gutterBottom>
                Items with Discrepancies ({discrepancies.length})
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                These items will have inventory adjustments applied as of {countDate}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">System Qty</TableCell>
                      <TableCell align="right">Counted Qty</TableCell>
                      <TableCell align="right">Variance</TableCell>
                      <TableCell>Batches</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discrepancies.map((item) => (
                      <TableRow key={item.sku}>
                        <TableCell>
                          <Typography fontWeight="medium">{item.sku}</Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(item.systemQty)}</TableCell>
                        <TableCell align="right">{formatNumber(item.countedQty)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={item.variance > 0 ? `+${item.variance}` : item.variance}
                            size="small"
                            color={item.variance > 0 ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          {item.batches.length > 0 ? (
                            <Typography variant="body2">
                              {item.batches.length} batch(es)
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="textSecondary">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Matches */}
        {matches.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography color="success.main">
                Items Matching System ({matches.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell>Batches</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matches.map((item) => (
                      <TableRow key={item.sku}>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell align="right">{formatNumber(item.countedQty)}</TableCell>
                        <TableCell>
                          {item.batches.length > 0 ? `${item.batches.length} batch(es)` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => {
              setCurrentStep(2);
              updateUrlParams({ step: '2' });
            }}
          >
            Back to Counting
          </Button>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <CompleteIcon />}
            onClick={handleSubmitCount}
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Count & Apply Adjustments'}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: embedded ? 2 : 0 }}>
      {/* Header - only show when not embedded in tab container */}
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Physical Counts
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {currentStep === 1 && 'Verify and reconcile inventory quantities'}
              {currentStep === 2 && 'Enter counted quantities for each item'}
              {currentStep === 3 && 'Review and submit your count'}
            </Typography>
          </Box>
          {currentStep === 1 && (
            <Tooltip title="Refresh">
              <IconButton onClick={loadWarehouses}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Progress indicator */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={1}>
          {['Setup', 'Count Items', 'Review & Submit'].map((label, idx) => (
            <Grid item xs={4} key={label}>
              <Paper
                sx={{
                  p: 1.5,
                  textAlign: 'center',
                  backgroundColor: currentStep > idx + 1 ? 'success.light' : currentStep === idx + 1 ? 'primary.light' : 'grey.200',
                  color: currentStep >= idx + 1 ? 'white' : 'text.secondary'
                }}
              >
                <Typography variant="body2" fontWeight={currentStep === idx + 1 ? 'bold' : 'normal'}>
                  {idx + 1}. {label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      )}

      {/* Step content */}
      {currentStep === 1 && renderSetup()}
      {currentStep === 2 && renderCounting()}
      {currentStep === 3 && renderReview()}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Count Data from CSV</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="medium">CSV Format:</Typography>
              <Typography variant="body2">
                sku, counted_quantity, batch_number (optional), expiry_date (optional)
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Multiple rows with same SKU but different batches will be combined.
              </Typography>
            </Alert>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              sx={{ mb: 3 }}
              fullWidth
            >
              Download Template
            </Button>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="import-file-upload"
            />
            <label htmlFor="import-file-upload">
              <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                Select CSV File
              </Button>
            </label>

            {importFile && (
              <Alert severity="info">
                Selected: {importFile.name}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportDialogOpen(false); setImportFile(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportCSV}
            disabled={!importFile || importing}
          >
            {importing ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Batch Dialog */}
      <Dialog open={addBatchDialogOpen} onClose={() => setAddBatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Batch for {currentSkuForBatch}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Batch Number"
              value={newBatch.batch_number}
              onChange={(e) => setNewBatch({ ...newBatch, batch_number: e.target.value })}
              fullWidth
              placeholder="e.g., BATCH-2024-001"
            />
            <TextField
              label="Expiry Date"
              type="date"
              value={newBatch.expiry_date}
              onChange={(e) => setNewBatch({ ...newBatch, expiry_date: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '2000-01-01' }}
            />
            <TextField
              label="Quantity"
              type="number"
              value={newBatch.quantity}
              onChange={(e) => setNewBatch({ ...newBatch, quantity: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddBatchDialogOpen(false);
            setNewBatch({ batch_number: '', expiry_date: '', quantity: '' });
            setCurrentSkuForBatch(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddBatch}
            disabled={!newBatch.quantity}
          >
            Add Batch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PhysicalCounts;
